import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InProgressTask, getInProgressTasks } from '../services/api';
import {
  SortState,
  compareValues,
  formatContractSumDisplay,
  formatDateShort,
  toggleSort,
} from '../utils/table';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import { formatDeadlineCompact, formatDeadlineRelativeTo } from '../utils/deadline';
import { useAuth } from '../context/AuthContext';
import {
  TASK_STATUS_FILTER_OPTIONS,
  TaskLifecycleStatus,
  getTaskStatusLabel,
  normalizeTaskStatus,
} from '../utils/taskStatus';
import '../styles/DataPage.css';

type InProgressTaskSortKey =
  | 'task_id'
  | 'short_name'
  | 'manager'
  | 'projectLead'
  | 'start_date'
  | 'end_date'
  | 'overdue'
  | 'task_status'
  | 'plan_expenses'
  | 'fact_expenses'
  | 'delta';

type InProgressTaskFilterMenuKey =
  | 'task_id'
  | 'short_name'
  | 'manager'
  | 'project_lead'
  | 'task_status'
  | null;

function taskDetailPath(task: Pick<InProgressTask, 'id' | 'contracts_task_id'>): string {
  return `/tasks/${task.id}?contracts_task_id=${task.contracts_task_id}`;
}

function formatOptionalSum(value: number | null | undefined): string {
  if (value == null) {
    return '—';
  }
  return formatContractSumDisplay(value);
}

/** Факт как на вкладке «Расходы», но с противоположным знаком и без «+». */
function displayedFactExpenses(value: number): number {
  return -value;
}

function formatFactExpensesDisplay(value: number): string {
  return formatContractSumDisplay(displayedFactExpenses(value));
}

const InProgressTasksPage: React.FC = () => {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [tasks, setTasks] = useState<InProgressTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<InProgressTaskFilterMenuKey>(null);

  const [searchText, setSearchText] = useState('');
  const [filterTaskIds, setFilterTaskIds] = useState<string[]>([]);
  const [filterNames, setFilterNames] = useState<string[]>([]);
  const [filterManagerIds, setFilterManagerIds] = useState<string[]>([]);
  const [filterLeadIds, setFilterLeadIds] = useState<string[]>([]);
  const [filterTaskStatuses, setFilterTaskStatuses] = useState<TaskLifecycleStatus[]>([]);
  const [minFactFilter, setMinFactFilter] = useState('');
  const [maxFactFilter, setMaxFactFilter] = useState('');

  const [sortState, setSortState] = useState<SortState<InProgressTaskSortKey>>({
    key: 'task_id',
    direction: 'desc',
  });

  const tableColSpan = 11;
  const canOpenPage = can('TASKS', 'VIEW') && can('OUTCOME', 'VIEW');

  const fetchData = useCallback(async (): Promise<void> => {
    if (!canOpenPage) {
      setIsLoading(false);
      setTasks([]);
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const data = await getInProgressTasks();
      setTasks(data.tasks);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при загрузке данных');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [canOpenPage]);

  useEffect(() => {
    if (canOpenPage) {
      void fetchData();
    } else {
      setIsLoading(false);
      setTasks([]);
    }
  }, [canOpenPage, fetchData]);

  const clearFilters = (): void => {
    setSearchText('');
    setFilterTaskIds([]);
    setFilterNames([]);
    setFilterManagerIds([]);
    setFilterLeadIds([]);
    setFilterTaskStatuses([]);
    setMinFactFilter('');
    setMaxFactFilter('');
    setFilterMenuOpen(null);
  };

  const hasActiveFilters =
    filterTaskIds.length > 0 ||
    filterNames.length > 0 ||
    filterManagerIds.length > 0 ||
    filterLeadIds.length > 0 ||
    filterTaskStatuses.length > 0 ||
    minFactFilter.trim() !== '' ||
    maxFactFilter.trim() !== '';

  useEffect(() => {
    if (!filtersOpen) {
      setFilterMenuOpen(null);
    }
  }, [filtersOpen]);

  const taskIdFilterOptions = useMemo(() => {
    const set = new Set(tasks.map((task) => task.task_id));
    return [...set].sort((a, b) => a.localeCompare(b, 'ru', { numeric: true }));
  }, [tasks]);

  const nameFilterOptions = useMemo(() => {
    const set = new Set(tasks.map((task) => task.short_name));
    return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
  }, [tasks]);

  const managerFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const task of tasks) {
      if (task.manager_id != null) {
        set.add(String(task.manager_id));
      }
    }
    return [...set].sort((a, b) => {
      const left = tasks.find((task) => String(task.manager_id) === a)?.manager_name ?? a;
      const right = tasks.find((task) => String(task.manager_id) === b)?.manager_name ?? b;
      return left.localeCompare(right, 'ru');
    });
  }, [tasks]);

  const projectLeadFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const task of tasks) {
      if (task.project_lead_id != null) {
        set.add(String(task.project_lead_id));
      }
    }
    return [...set].sort((a, b) => {
      const left = tasks.find((task) => String(task.project_lead_id) === a)?.project_lead_name ?? a;
      const right = tasks.find((task) => String(task.project_lead_id) === b)?.project_lead_name ?? b;
      return left.localeCompare(right, 'ru');
    });
  }, [tasks]);

  const getManagerLabel = useCallback(
    (id: string): string => tasks.find((task) => String(task.manager_id) === id)?.manager_name ?? id,
    [tasks],
  );

  const getLeadLabel = useCallback(
    (id: string): string => tasks.find((task) => String(task.project_lead_id) === id)?.project_lead_name ?? id,
    [tasks],
  );

  const getDeadline = useCallback((task: InProgressTask) => {
    return formatDeadlineRelativeTo(task.end_date, task.expense_closing_date);
  }, []);

  const filteredTasks = useMemo(() => {
    const text = searchText.trim().toLowerCase();

    return tasks.filter((task) => {
      const deadline = getDeadline(task);
      const searchable = [
        task.task_id,
        task.short_name,
        task.manager_name,
        task.project_lead_name,
        task.start_date ? formatDateShort(task.start_date) : '',
        task.end_date ? formatDateShort(task.end_date) : '',
        deadline.label ? deadline.label.toLowerCase() : '',
        formatDeadlineCompact(deadline),
        getTaskStatusLabel(normalizeTaskStatus(task.task_status)),
        formatOptionalSum(task.plan_expenses),
        formatFactExpensesDisplay(task.fact_expenses),
        formatOptionalSum(task.delta),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const minFact = minFactFilter ? Number(minFactFilter) : Number.NEGATIVE_INFINITY;
      const maxFact = maxFactFilter ? Number(maxFactFilter) : Number.POSITIVE_INFINITY;
      const factDisplayed = displayedFactExpenses(task.fact_expenses);

      const matchesSearch = !text || searchable.includes(text);
      const matchesTaskId = filterTaskIds.length === 0 || filterTaskIds.includes(task.task_id);
      const matchesName = filterNames.length === 0 || filterNames.includes(task.short_name);
      const matchesManager =
        filterManagerIds.length === 0 || filterManagerIds.includes(String(task.manager_id || ''));
      const matchesLead =
        filterLeadIds.length === 0 || filterLeadIds.includes(String(task.project_lead_id || ''));
      const matchesTaskStatus =
        filterTaskStatuses.length === 0 ||
        filterTaskStatuses.includes(normalizeTaskStatus(task.task_status));
      const matchesFact = factDisplayed >= minFact && factDisplayed <= maxFact;

      return (
        matchesSearch &&
        matchesTaskId &&
        matchesName &&
        matchesManager &&
        matchesLead &&
        matchesTaskStatus &&
        matchesFact
      );
    });
  }, [
    tasks,
    searchText,
    filterTaskIds,
    filterNames,
    filterManagerIds,
    filterLeadIds,
    filterTaskStatuses,
    minFactFilter,
    maxFactFilter,
    getDeadline,
  ]);

  const sortedTasks = useMemo(() => {
    const getSortValue = (task: InProgressTask, key: InProgressTaskSortKey): string | number | undefined => {
      const deadline = getDeadline(task);
      const values: Record<InProgressTaskSortKey, string | number | undefined> = {
        task_id: task.task_id,
        short_name: task.short_name,
        manager: task.manager_name,
        projectLead: task.project_lead_name,
        start_date: task.start_date ?? undefined,
        end_date: task.end_date ?? undefined,
        overdue: task.end_date ?? undefined,
        task_status: getTaskStatusLabel(normalizeTaskStatus(task.task_status)),
        plan_expenses: task.plan_expenses ?? undefined,
        fact_expenses: displayedFactExpenses(task.fact_expenses),
        delta: task.delta ?? undefined,
      };
      if (key === 'overdue') {
        return deadline.overdue
          ? -(deadline.months ?? 0) * 30 - (deadline.days ?? 0)
          : (deadline.months ?? 0) * 30 + (deadline.days ?? 0);
      }
      return values[key];
    };

    const { key: sortKey, direction: sortDirection } = sortState;
    return [...filteredTasks].sort((left, right) =>
      compareValues(getSortValue(left, sortKey), getSortValue(right, sortKey), sortDirection),
    );
  }, [filteredTasks, sortState, getDeadline]);

  const footerTotals = useMemo(() => {
    let factExpenses = 0;
    for (const task of sortedTasks) {
      factExpenses += task.fact_expenses;
    }
    return { factExpenses };
  }, [sortedTasks]);

  const handleSort = (key: InProgressTaskSortKey): void => {
    setSortState((current) => toggleSort(current, key));
  };

  return (
    <div className="data-page contracts-page in-progress-contracts-page in-progress-tasks-page">
      {!canOpenPage ? (
        <div className="empty-state">Нет доступа к странице (нужны права TASKS VIEW и OUTCOME VIEW).</div>
      ) : (
        <>
      <div className="page-header-row">
        <h1>Это все доступные тебе активные задачи</h1>
        <div className="page-header-row__actions">
          <button
            type="button"
            className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
          >
            Фильтры
            <span className="filters-toggle-badge" aria-hidden />
          </button>
        </div>
        <div className="page-header-row__search">
          <input
            type="text"
            className="search-input"
            placeholder="Поиск по текстовым полям"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
        {filtersOpen && (
          <aside className="filters-panel">
            <div className="filters-header">
              <h2>Фильтры</h2>
              <button type="button" className="link-button" onClick={clearFilters}>
                Сбросить
              </button>
            </div>
            <div className="filters-grid">
              <div className="filters-field">
                <SheetsColumnMultiFilter
                  label="ID задачи"
                  options={taskIdFilterOptions}
                  selected={filterTaskIds}
                  onSelectedChange={setFilterTaskIds}
                  formatOption={(v) => v}
                  isOpen={filterMenuOpen === 'task_id'}
                  onOpenChange={(open) => setFilterMenuOpen(open ? 'task_id' : null)}
                />
              </div>
              <div className="filters-field">
                <SheetsColumnMultiFilter
                  label="Название задачи"
                  options={nameFilterOptions}
                  selected={filterNames}
                  onSelectedChange={setFilterNames}
                  formatOption={(v) => v}
                  isOpen={filterMenuOpen === 'short_name'}
                  onOpenChange={(open) => setFilterMenuOpen(open ? 'short_name' : null)}
                />
              </div>
              <div className="filters-field">
                <SheetsColumnMultiFilter
                  label="Менеджер"
                  options={managerFilterOptions}
                  selected={filterManagerIds}
                  onSelectedChange={setFilterManagerIds}
                  formatOption={getManagerLabel}
                  isOpen={filterMenuOpen === 'manager'}
                  onOpenChange={(open) => setFilterMenuOpen(open ? 'manager' : null)}
                />
              </div>
              <div className="filters-field">
                <SheetsColumnMultiFilter
                  label="РП"
                  options={projectLeadFilterOptions}
                  selected={filterLeadIds}
                  onSelectedChange={setFilterLeadIds}
                  formatOption={getLeadLabel}
                  isOpen={filterMenuOpen === 'project_lead'}
                  onOpenChange={(open) => setFilterMenuOpen(open ? 'project_lead' : null)}
                />
              </div>
              <div className="filters-field">
                <SheetsColumnMultiFilter
                  label="Статус"
                  options={TASK_STATUS_FILTER_OPTIONS}
                  selected={filterTaskStatuses}
                  onSelectedChange={(next) => setFilterTaskStatuses(next as TaskLifecycleStatus[])}
                  formatOption={(v) => getTaskStatusLabel(v)}
                  isOpen={filterMenuOpen === 'task_status'}
                  onOpenChange={(open) => setFilterMenuOpen(open ? 'task_status' : null)}
                />
              </div>
              <label className="filters-sum-block">
                <span className="filters-sum-block__title">Факт расходы</span>
                <div className="filters-sum-block__row">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="filters-sum-block__input"
                    placeholder="от"
                    value={minFactFilter}
                    onChange={(e) => setMinFactFilter(e.target.value)}
                  />
                  <input
                    type="text"
                    inputMode="decimal"
                    className="filters-sum-block__input"
                    placeholder="до"
                    value={maxFactFilter}
                    onChange={(e) => setMaxFactFilter(e.target.value)}
                  />
                </div>
              </label>
            </div>
          </aside>
        )}

        <section className="table-content">
          {isLoading ? (
            <div className="loading">Загрузка...</div>
          ) : (
            <div className="table-panel">
              <div className="table-scroll">
                <table className="data-table contracts-page__table in-progress-contracts-page__table in-progress-tasks-page__table">
                  <colgroup>
                    <col className="contracts-page__colgrid in-progress-tasks-page__colgrid--task-id" />
                    <col className="contracts-page__colgrid in-progress-tasks-page__colgrid--name" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--person" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--person" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--date" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--date" />
                    <col className="contracts-page__colgrid in-progress-contracts-page__colgrid--overdue" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--status" />
                    <col className="contracts-page__colgrid in-progress-tasks-page__colgrid--finance" />
                    <col className="contracts-page__colgrid in-progress-tasks-page__colgrid--finance" />
                    <col className="contracts-page__colgrid in-progress-tasks-page__colgrid--finance" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleSort('task_id')} role="button">
                        ID задачи
                      </th>
                      <th className="sortable" onClick={() => handleSort('short_name')} role="button">
                        Название задачи
                      </th>
                      <th
                        className="sortable contracts-page__th-person"
                        onClick={() => handleSort('manager')}
                        role="button"
                      >
                        Менеджер
                      </th>
                      <th
                        className="sortable contracts-page__th-person"
                        onClick={() => handleSort('projectLead')}
                        role="button"
                      >
                        РП
                      </th>
                      <th
                        className="sortable contracts-page__th-date"
                        onClick={() => handleSort('start_date')}
                        role="button"
                      >
                        Начало
                      </th>
                      <th
                        className="sortable contracts-page__th-date"
                        onClick={() => handleSort('end_date')}
                        role="button"
                      >
                        Срок
                      </th>
                      <th
                        className="sortable in-progress-contracts-page__th-overdue"
                        onClick={() => handleSort('overdue')}
                        role="button"
                      >
                        Просрочка
                      </th>
                      <th
                        className="sortable contracts-page__th-status"
                        onClick={() => handleSort('task_status')}
                        role="button"
                      >
                        Статус
                      </th>
                      <th className="sortable col-num" onClick={() => handleSort('plan_expenses')} role="button">
                        План расходы
                      </th>
                      <th className="sortable col-num" onClick={() => handleSort('fact_expenses')} role="button">
                        Факт расходы
                      </th>
                      <th className="sortable col-num" onClick={() => handleSort('delta')} role="button">
                        Дельта
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTasks.length === 0 ? (
                      <tr>
                        <td colSpan={tableColSpan} className="data-table__empty">
                          Ничего не найдено
                        </td>
                      </tr>
                    ) : (
                      sortedTasks.map((task) => {
                        const deadline = getDeadline(task);
                        const deltaNegative = task.delta != null && task.delta < 0;
                        return (
                          <tr
                            key={task.id}
                            className="clickable-row contracts-page__row--main"
                            onClick={(e) => {
                              if (e.ctrlKey || e.metaKey) {
                                return;
                              }
                              navigate(taskDetailPath(task));
                            }}
                          >
                            <td>
                              <div className="contracts-page__clamp-2">
                                <strong>{task.task_id}</strong>
                              </div>
                            </td>
                            <td>
                              <div className="contracts-page__clamp-2">{task.short_name || '—'}</div>
                            </td>
                            <td className="contracts-page__td-person">
                              <div className="contracts-page__clamp-2">{task.manager_name}</div>
                            </td>
                            <td className="contracts-page__td-person">
                              <div className="contracts-page__clamp-2">{task.project_lead_name}</div>
                            </td>
                            <td className="contracts-page__td-date">
                              {task.start_date ? formatDateShort(task.start_date) : '—'}
                            </td>
                            <td className="contracts-page__td-date">
                              {task.end_date ? formatDateShort(task.end_date) : '—'}
                            </td>
                            <td
                              className={`in-progress-contracts-page__td-overdue${deadline.overdue ? ' in-progress-contracts-page__cell--overdue' : ''}`}
                            >
                              {deadline.label ? (
                                <div className="contracts-page__clamp-2 in-progress-contracts-page__deadline">
                                  <span className="in-progress-contracts-page__deadline-label">
                                    {deadline.label.toLowerCase()}
                                  </span>
                                  <span>{formatDeadlineCompact(deadline)}</span>
                                </div>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="contracts-page__td-status">
                              {getTaskStatusLabel(normalizeTaskStatus(task.task_status))}
                            </td>
                            <td className="col-num">{formatOptionalSum(task.plan_expenses)}</td>
                            <td className="col-num">{formatFactExpensesDisplay(task.fact_expenses)}</td>
                            <td
                              className={`col-num${deltaNegative ? ' in-progress-contracts-page__cell--negative' : ''}`}
                            >
                              {formatOptionalSum(task.delta)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className="table-panel__footer">
                <table className="data-table data-table--footer contracts-page__table in-progress-contracts-page__table in-progress-tasks-page__table">
                  <colgroup>
                    <col className="contracts-page__colgrid in-progress-tasks-page__colgrid--task-id" />
                    <col className="contracts-page__colgrid in-progress-tasks-page__colgrid--name" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--person" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--person" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--date" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--date" />
                    <col className="contracts-page__colgrid in-progress-contracts-page__colgrid--overdue" />
                    <col className="contracts-page__colgrid contracts-page__colgrid--status" />
                    <col className="contracts-page__colgrid in-progress-tasks-page__colgrid--finance" />
                    <col className="contracts-page__colgrid in-progress-tasks-page__colgrid--finance" />
                    <col className="contracts-page__colgrid in-progress-tasks-page__colgrid--finance" />
                  </colgroup>
                  <tfoot>
                    <tr>
                      <td>Итого: {sortedTasks.length}</td>
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td className="col-num">—</td>
                      <td className="col-num">{formatFactExpensesDisplay(footerTotals.factExpenses)}</td>
                      <td className="col-num">—</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </section>
      </div>
        </>
      )}
    </div>
  );
};

export default InProgressTasksPage;
